/* Derived view-model for the home route.
 * GENERATED from the original artifact's vals_home() by
 * tools/convert-logic.py; edits since generation are tracked in git. */
import { CLUSTERS, FOOTER_COLS, SECTIONS, STATES, sessionRows } from '@/data/wft';
import type { ClusterId } from '@/data/wft';

export function homeVals(now: Date | null) {
  const sessions = sessionRows(now);
  const clusters = (['tape', 'context', 'engine', 'desk'] as ClusterId[]).map((c) => {
    const items = SECTIONS.filter((x) => x.cluster === c);
    return {
      label: CLUSTERS[c].label,
      blurb: CLUSTERS[c].blurb,
      count: items.length + ' sections',
      sections: items.map((x) => ({
        mnemonic: x.mnemonic,
        title: x.title,
        badge: x.live ? '' : 'NC',
      })),
      bars: items.map((x) => ({
        title:
          x.title + ' — ' + x.panels.length + ' panels' + (x.live ? '' : ' · no connector at M1'),
        color: x.live ? 'var(--wft-engine-dim)' : 'var(--wft-accent)',
      })),
    };
  });

  return {
    sessions,
    clusters,
    footerCols: FOOTER_COLS,
    states: STATES,
    figures: [
      {
        value: '20',
        label: 'Sections',
        note: 'Tape, context, engine and desk, plus two system pages.',
      },
      {
        value: '17',
        label: 'Connectors',
        note: 'Each declaring cadence, key requirement and redistribution terms.',
      },
      {
        value: '2',
        label: 'Engines',
        note: 'TimesFM 2.5 quantile forecasts and the GodEngine swarm resolver.',
      },
      {
        value: '925 MB',
        label: 'Weights',
        note: '925,181,104 bytes, checksummed before an atomic rename.',
      },
      {
        value: '3',
        label: 'Densities',
        note: 'Compact, normal, relaxed — relaxed doubles as the touch profile.',
      },
      {
        value: '0',
        label: 'Fake numbers',
        note: 'Enforced in CI, in types and in the contract tests.',
      },
    ],
    guards: [
      {
        rule: 'Every data component requires an emptyState prop',
        where: 'TypeScript, compile-time',
      },
      {
        rule: 'Unavailable endpoints must return data is None, never []',
        where: 'tests/contract/',
      },
      { rule: 'No faker / sample_data / lorem outside tests/', where: 'CI workflow' },
      {
        rule: 'A forecast from the fallback decoder draws no line at all',
        where: 'decoder_name reaches the browser',
      },
    ],
    pipeline: [
      {
        n: '01',
        stage: 'Sources',
        color: 'var(--wft-ink-1)',
        arrow: '→',
        items: ['17 connectors', 'cadence + key declared', 'redistribution recorded'],
        note: 'wft/sources/',
      },
      {
        n: '02',
        stage: 'Workers',
        color: 'var(--wft-ink-1)',
        arrow: '→',
        items: ['scheduled fetch', 'normalise to series keys', 'partial fetch marked partial'],
        note: 'wft/worker/',
      },
      {
        n: '03',
        stage: 'Store',
        color: 'var(--wft-ink-1)',
        arrow: '→',
        items: ['Postgres :5433', 'Redis :6380', 'own migration lineage'],
        note: 'wft/db/ · alembic/',
      },
      {
        n: '04',
        stage: 'Engine',
        color: 'var(--wft-engine)',
        arrow: '→',
        items: ['TimesFM 2.5 forecast', 'swarm scenario resolve', 'decoder_name recorded'],
        note: 'wft/forecast/ · wft/godengine/',
      },
      {
        n: '05',
        stage: 'API + terminal',
        color: 'var(--wft-accent)',
        arrow: '',
        items: [
          'envelopes may say data is None',
          'panel picks the honest render',
          '20 sections, one registry',
        ],
        note: 'wft/api/ · frontend/',
      },
    ],
    lanes: [
      {
        name: 'TimesFM 2.5',
        color: 'var(--wft-engine)',
        module: 'temporal/ · wft/forecast/',
        body: 'A foundation time-series model, vendored byte-verbatim from god-engine and verified against a lockfile in CI. Produces the quantile fan and the median for any series in the product.',
        feeds: 'Forecast Lab · every forecast column · Divergence · Record',
      },
      {
        name: 'Swarm resolve',
        color: 'var(--wft-scenario)',
        module: 'wft/godengine/client.py',
        body: 'GodEngine\u2019s resolver streams branching futures with probability mass on each branch, plus the assumptions and triggers behind them. Different question, different primitive, same engine ink.',
        feeds: 'Scenarios · Simulation · Regimes',
      },
    ],
    engineInk: [
      {
        label: 'Engine',
        color: 'var(--wft-engine)',
        note: 'Model median. Never an observed number.',
      },
      { label: 'Baseline', color: 'var(--wft-baseline)', note: 'Random walk, for skill.' },
      { label: 'Consensus', color: 'var(--wft-consensus)', note: 'Reserved for survey medians.' },
      { label: 'Scenario', color: 'var(--wft-scenario)', note: 'Branch paths from the swarm.' },
    ],
    scoreCards: [
      { label: 'Resolved', note: 'Horizon passed and scored.' },
      { label: 'Pending', note: 'Written down, outcome unknown.' },
      { label: 'Coverage @80', note: 'How often reality landed inside the band.' },
      { label: 'Skill', note: 'Versus a random walk.' },
    ],
    densities: [
      {
        name: 'compact',
        meta: '20px rows · 4px gutter',
        rows: [{ h: '20px' }, { h: '20px' }, { h: '20px' }, { h: '20px' }, { h: '20px' }],
      },
      {
        name: 'normal',
        meta: '24px rows · 6px gutter',
        rows: [{ h: '24px' }, { h: '24px' }, { h: '24px' }, { h: '24px' }],
      },
      {
        name: 'relaxed',
        meta: '30px rows · 44px targets',
        rows: [{ h: '30px' }, { h: '30px' }, { h: '30px' }],
      },
    ],
    keys: [
      { combo: 'g then c', does: 'Cockpit — g arms, next key jumps' },
      { combo: 'g then e', does: 'Forecast Lab' },
      { combo: 'g then k', does: 'Track Record' },
      { combo: 'g then v', does: 'Sources board' },
      { combo: 'Ctrl B', does: 'Collapse the rail' },
      { combo: 'Ctrl ⇧ T', does: 'Terminal / Canon theme' },
      { combo: 'Ctrl ⇧ D', does: 'Cycle density' },
      { combo: '?', does: 'Keyboard cheat sheet' },
    ],
  };
}

export type HomeVals = ReturnType<typeof homeVals>;
